import { Injectable } from '@angular/core';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { db } from '../config/firebase.config';
import { doc, getDoc, setDoc, updateDoc, collection, addDoc, serverTimestamp, query, orderBy, limit as fbLimit, getDocs, deleteDoc } from 'firebase/firestore';
import { FileUploadService, UploadResult } from './file-upload.service';

export interface HomeAssetsByLangItem {
  url: string;
  path: string;
}

export interface HomeAssets {
  // default language mandatory values saved under 'default'
  banner: { [lang: string]: HomeAssetsByLangItem };
  offers: { [lang: string]: HomeAssetsByLangItem[] }; // Changed to array for multiple offers
  updatedAt: number;
}

export interface SiteContentUploadItem {
  id?: string;
  type: 'banner' | 'offer';
  lang: string;
  url: string;
  path: string;
  contentType: string;
  sizeBytes: number;
  uploadedAt: number;
}

@Injectable({ providedIn: 'root' })
export class SiteContentService {
  private collectionName = 'siteContent';
  private docId = 'home';

  constructor(public fileUploadService: FileUploadService) {}

  getHomeAssets(): Observable<HomeAssets | null> {
    const ref = doc(db, this.collectionName, this.docId);
    return from(getDoc(ref)).pipe(
      map(snap => (snap.exists() ? (snap.data() as HomeAssets) : null))
    );
  }

  async saveHomeAssets(partial: Partial<HomeAssets>): Promise<void> {
    const ref = doc(db, this.collectionName, this.docId);
    const now = Date.now();
    const existing = await getDoc(ref);
    if (existing.exists()) {
      await updateDoc(ref, { ...partial, updatedAt: now } as any);
    } else {
      await setDoc(ref, { banner: {}, offer: {}, updatedAt: now, ...partial } as any);
    }
  }

  uploadBanner(file: File): Observable<UploadResult> {
    return this.fileUploadService.uploadFileToFolder(file, 'site-content/banners');
  }

  uploadOffer(file: File): Observable<UploadResult> {
    return this.fileUploadService.uploadFileToFolder(file, 'site-content/offers');
  }

  uploadBannerWithProgress(file: File) {
    return this.fileUploadService.uploadFileToFolderWithProgress(file, 'site-content/banners');
  }

  uploadOfferWithProgress(file: File) {
    return this.fileUploadService.uploadFileToFolderWithProgress(file, 'site-content/offers');
  }

  async recordUpload(type: 'banner' | 'offer', result: UploadResult, file: File, lang: string): Promise<void> {
    if (!result.success || !result.downloadURL || !result.filePath) return;
    const uploadsCol = collection(db, 'siteContentUploads');
    await addDoc(uploadsCol, {
      type,
      lang,
      url: result.downloadURL,
      path: result.filePath,
      contentType: file.type || '',
      sizeBytes: file.size || 0,
      uploadedAt: Date.now()
    });
  }

  getRecentUploads(limitCount: number = 20): Observable<SiteContentUploadItem[]> {
    const uploadsCol = collection(db, 'siteContentUploads');
    return from(getDocs(query(uploadsCol, orderBy('uploadedAt', 'desc'), fbLimit(limitCount))))
      .pipe(map(snap => snap.docs.map(d => ({ id: d.id, ...(d.data() as SiteContentUploadItem) }))));
  }

  async saveBannerForLanguage(lang: string, item: HomeAssetsByLangItem): Promise<void> {
    const ref = doc(db, this.collectionName, this.docId);
    const now = Date.now();
    const path = `banner.${lang}` as any;
    await updateDoc(ref, { [path]: item, updatedAt: now } as any).catch(async () => {
      await setDoc(ref, { banner: { [lang]: item }, offer: {}, updatedAt: now } as any, { merge: true } as any);
    });
  }

  async saveOfferForLanguage(lang: string, item: HomeAssetsByLangItem): Promise<void> {
    const ref = doc(db, this.collectionName, this.docId);
    const now = Date.now();
    
    // Get existing offers for this language
    const docSnap = await getDoc(ref);
    const existingData = docSnap.exists() ? (docSnap.data() as any) : {};
    const existingOffers = existingData.offers?.[lang] || [];
    
    // Add new offer to the array
    const updatedOffers = [...existingOffers, item];
    const path = `offers.${lang}` as any;
    
    await updateDoc(ref, { [path]: updatedOffers, updatedAt: now } as any).catch(async () => {
      await setDoc(ref, { banner: {}, offers: { [lang]: updatedOffers }, updatedAt: now } as any, { merge: true } as any);
    });
  }

  // Delete specific offer image for a language
  async deleteOfferForLanguage(lang: string, offerIndex: number): Promise<void> {
    const ref = doc(db, this.collectionName, this.docId);
    const docSnap = await getDoc(ref);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as any;
      const offers = data.offers?.[lang] || [];
      
      if (offerIndex >= 0 && offerIndex < offers.length) {
        // Remove the offer at the specified index
        const updatedOffers = offers.filter((_: any, index: number) => index !== offerIndex);
        const path = `offers.${lang}` as any;
        
        if (updatedOffers.length === 0) {
          // If no offers left, remove the language key entirely
          await updateDoc(ref, { [path]: null } as any);
        } else {
          await updateDoc(ref, { [path]: updatedOffers } as any);
        }
      }
    }
  }

  // Migration method to convert 'default' to 'en' if needed
  async migrateDefaultToEnglish(): Promise<void> {
    const ref = doc(db, this.collectionName, this.docId);
    const docSnap = await getDoc(ref);
    
    if (docSnap.exists()) {
      const data = docSnap.data() as any;
      let needsUpdate = false;
      const updates: any = {};

      // Check if 'default' exists and migrate to 'en'
      if (data.banner?.default && !data.banner?.en) {
        updates['banner.en'] = data.banner.default;
        updates['banner.default'] = null;
        needsUpdate = true;
      }
      
      if (data.offer?.default && !data.offer?.en) {
        updates['offers.en'] = [data.offer.default]; // Convert single offer to array
        updates['offer.default'] = null;
        needsUpdate = true;
      }

      if (needsUpdate) {
        await updateDoc(ref, updates);
        console.log('Migrated default language to English');
      }
    }
  }

  async deleteUpload(item: SiteContentUploadItem): Promise<boolean> {
    try {
      // Delete from Firebase Storage
      await this.fileUploadService.deleteFile(item.path).toPromise();
      
      // Delete from Firestore
      if (item.id) {
        await deleteDoc(doc(db, 'siteContentUploads', item.id));
      }
      
      return true;
    } catch (error) {
      console.error('Error deleting upload:', error);
      return false;
    }
  }
}


